/**
 * /academy/progress — « Mon profil / Ma progression »: evolution curve, XP and streak, rank
 * among all the learners of the platform and within the agency, pointed analysis with the
 * readiness verdict and its critical points, detail per skill, complete report (PDF).
 * Everything shown is an estimate: never a guarantee of score.
 */

import {
  ApiError,
  useAcademyFormatRetrieve,
  useAcademyMeRetrieve,
  useAcademyProgressRetrieve,
  useAcademyReportCreate,
  useAcademyReportRetrieve,
} from "@agencyos/api-client";
import { Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  FileDown,
  Flame,
  MapPin,
  Medal,
  Minus,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  UserRound,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { CenterCard } from "./CenterPicker";
import { EvolutionChart } from "./EvolutionChart";
import { RankScoreCard } from "./RankScoreCard";
import { type CriticalPoint, type RankBlock, type Trend, type Verdict, parseProgress } from "./progressTypes";
import { dateText } from "./shared";
import { SKILLS, localized, parseRubrics } from "./types";
import { ActivityRings, CardTitle, PageHead } from "./ui";

type TFn = ReturnType<typeof useTranslation>["t"];

/** The text of a critical point, from its code and parameters (server message as a fallback). */
export function criticalText(point: CriticalPoint, t: TFn, criterionLabel: (key: string) => string): string {
  const skill = point.skill ? t(`academy.skill.${point.skill}`) : "";
  const percent = (value: number | null) => Math.round((value ?? 0) * 100);
  switch (point.code) {
    case "not_measured":
      return t("academy.critical.not_measured", { skill });
    case "below_target":
      return t("academy.critical.below_target", { skill, count: point.gap ?? 1 });
    case "declining":
      return t("academy.critical.declining", { skill });
    case "weak_level":
      return t("academy.critical.weak_level", { skill, level: point.level, percent: percent(point.success) });
    case "weak_criterion":
      return t("academy.critical.weak_criterion", {
        skill,
        criterion: criterionLabel(point.criterion),
        percent: percent(point.average),
      });
    case "word_count":
      return t("academy.critical.word_count", { count: point.count ?? 1 });
    case "time":
      return t("academy.critical.time", { count: point.count ?? 1 });
    case "exam_too_soon":
      return t("academy.critical.exam_too_soon", {
        days: t("academy.critical.days", { count: Math.max(0, point.daysLeft ?? 0) }),
        weeks: t("academy.critical.weeks", { count: point.weeksNeeded ?? 1 }),
      });
    default:
      return point.message;
  }
}

function RankTile({ title, block, icon }: { title: string; block: RankBlock | null; icon: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="ac-stat">
      <span className="ac-pastille ac-tone--info" aria-hidden="true">
        {icon}
      </span>
      <div className="ac-stat__body">
        <p className="ac-stat__label">{title}</p>
        {block ? (
          <>
            <p className="ac-stat__value ao-num">
              {t("academy.progress.position", { count: block.position, ordinal: true })}
              <span className="ac-stat__unit"> {t("academy.progress.outOf", { total: block.total })}</span>
            </p>
            <p className="ac-stat__trend">{t("academy.progress.top", { percent: block.topPercent })}</p>
          </>
        ) : (
          <p className="ac-footnote">{t("academy.progress.notRanked")}</p>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, icon, tone }: { label: string; value: string; icon: ReactNode; tone: string }) {
  return (
    <div className="ac-stat">
      <span className={`ac-pastille ac-tone--${tone}`} aria-hidden="true">
        {icon}
      </span>
      <div className="ac-stat__body">
        <p className="ac-stat__label">{label}</p>
        <p className="ac-stat__value ao-num">{value}</p>
      </div>
    </div>
  );
}

const TREND_ICON: Record<Trend, typeof ArrowUpRight> = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  stable: ArrowRight,
  unknown: Minus,
};

const VERDICT_TONE: Record<Verdict, "success" | "warning" | "danger"> = {
  ready: "success",
  almost: "warning",
  not_ready: "danger",
};

const REPORT_POLL_MS = 3000;
const REPORT_POLL_LIMIT_MS = 60_000;

/**
 * « Télécharger mon rapport complet »: POST prints a new PDF in the background, then GET
 * academy/report is asked every 3 s (60 s at most) until its signed link is there.
 */
export function ReportButton({ exam }: { exam?: "ielts_general" | "tef_canada" | "goethe" }) {
  const { t, i18n } = useTranslation();
  const create = useAcademyReportCreate();
  const [requested, setRequested] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const report = useAcademyReportRetrieve(exam ? { exam } : undefined, {
    query: {
      retry: false,
      refetchInterval: (query) => {
        if (!requested || timedOut) return false;
        const data = query.state.data?.status === 200 ? query.state.data.data : undefined;
        const done = data && data.id === requested && (Boolean(data.url) || data.status === "failed");
        return done ? false : REPORT_POLL_MS;
      },
    },
  });
  const latest = report.data?.status === 200 ? report.data.data : undefined;
  const waiting = requested !== null && !timedOut && !(latest?.id === requested && (latest.url || latest.status === "failed"));

  useEffect(() => {
    if (!requested) return;
    setTimedOut(false);
    const timer = window.setTimeout(() => setTimedOut(true), REPORT_POLL_LIMIT_MS);
    return () => window.clearTimeout(timer);
  }, [requested]);

  const ask = () =>
    create.mutate(exam ? { params: { exam } } : {}, {
      onSuccess: (response) => {
        setRequested(response.data.id);
        void report.refetch();
      },
    });

  const failed = requested !== null && latest?.id === requested && latest.status === "failed";
  const createError = create.error instanceof ApiError ? (create.error.detail ?? t("common.error")) : create.error ? t("common.error") : null;

  return (
    <div className="ac-report">
      <p className="ac-secondary">{t("academy.report.hint")}</p>
      <div className="ac-card__actions ac-card__actions--start">
        <Button variant="secondary" icon={<FileDown aria-hidden size={18} />} onClick={ask} disabled={create.isPending || waiting}>
          {create.isPending || waiting ? t("common.loading") : t("academy.report.download")}
        </Button>
      </div>
      <div aria-live="polite">
        {waiting && (
          <p className="ac-status ac-secondary">
            <span className="ac-dot ac-dot--busy" aria-hidden="true" />
            {t("academy.report.preparing")}
          </p>
        )}
        {failed && <p className="ko">{t("academy.report.failed")}</p>}
        {requested !== null && timedOut && !latest?.url && !failed && (
          <p className="ac-secondary">{t("academy.report.slow")}</p>
        )}
        {!waiting && latest?.url && (
          <p className={requested && latest.id === requested ? "ok" : "ac-secondary"}>
            {requested && latest.id === requested
              ? t("academy.report.ready")
              : t("academy.report.last", { date: dateText(latest.created_at, i18n.language) })}{" "}
            <a className="ac-more" href={latest.url} target="_blank" rel="noreferrer">
              <FileDown aria-hidden size={16} />
              {t("academy.report.open")}
            </a>
          </p>
        )}
      </div>
      {createError && (
        <p className="ko" role="alert">
          {createError}
        </p>
      )}
    </div>
  );
}

export function ProgressPage() {
  const { t, i18n } = useTranslation();
  const me = useAcademyMeRetrieve({ query: { retry: false } });
  const query = useAcademyProgressRetrieve(undefined, { query: { retry: false } });
  const format = useAcademyFormatRetrieve(undefined, { query: { staleTime: 3_600_000, retry: false } });
  const rubrics = useMemo(
    () => parseRubrics(format.data?.status === 200 ? format.data.data.rubrics : undefined),
    [format.data],
  );
  const progress = useMemo(() => (query.data?.status === 200 ? parseProgress(query.data.data) : null), [query.data]);
  const learner = me.data?.status === 200 ? me.data.data : undefined;

  if (query.isLoading || me.isLoading) {
    return (
      <div className="ac-page">
        <Skeleton style={{ height: 72, maxWidth: 420 }} />
        <div className="ac-grid">
          <Skeleton className="ac-span-7" style={{ height: 300 }} />
          <Skeleton className="ac-span-5" style={{ height: 300 }} />
        </div>
      </div>
    );
  }
  if (!progress || !learner) {
    return (
      <GlassCard className="ac-card ac-card--center">
        <p className="ko">{t("common.error")}</p>
        <Button variant="secondary" onClick={() => void query.refetch()}>
          {t("common.retry")}
        </Button>
      </GlassCard>
    );
  }

  const { analysis } = progress;
  const target = learner.target_nclc ?? analysis.target;
  const criterionLabel = (key: string) => {
    for (const rows of Object.values(rubrics)) {
      const row = rows.find((r) => r.key === key);
      if (row) return localized(row.label, i18n.language) || key;
    }
    return key;
  };
  const VerdictIcon = analysis.verdict === "ready" ? CheckCircle2 : analysis.verdict === "almost" ? Target : AlertTriangle;
  const reached = SKILLS.filter((s) => {
    const n = analysis.skills[s].nclc;
    return n !== null && n >= target;
  }).length;

  return (
    <div className="ac-page ac-progress-page">
      <PageHead
        eyebrow={t("academy.progress.eyebrow")}
        title={t("academy.progress.title")}
        subtitle={
          <ul className="ac-meta-list ac-meta-list--inline">
            <li>
              <UserRound aria-hidden size={15} />
              <span>{learner.full_name || learner.email}</span>
            </li>
            <li>
              <Target aria-hidden size={15} />
              <span>{t("academy.goals.nclc", { n: target })}</span>
            </li>
            <li>
              <CalendarDays aria-hidden size={15} />
              <span>{learner.exam_date ? dateText(learner.exam_date, i18n.language) : t("academy.progress.notSet")}</span>
            </li>
            <li>
              <MapPin aria-hidden size={15} />
              <span>{learner.exam_center || t("academy.progress.notSet")}</span>
            </li>
          </ul>
        }
        aside={
          <Link to="/academy/home" className="ac-more">
            {t("academy.progress.editGoals")}
            <ChevronRight aria-hidden size={16} />
          </Link>
        }
      />
      <p className="ac-note" role="note">
        {t("academy.estimateNotice")}
      </p>

      {!learner.exam_center && <CenterCard me={learner} />}

      <GlassCard as="section" className="ac-card ac-fitness" aria-labelledby="academy-skills">
        <div className="ac-card__head">
          <CardTitle id="academy-skills" icon={<Target size={16} />}>
            {t("academy.progress.perSkill")}
          </CardTitle>
          <span className="ac-caption ao-num">{t("academy.ui.progress.reached", { count: reached, total: SKILLS.length })}</span>
        </div>
        <div className="ac-fitness__body">
          <ActivityRings
            rings={SKILLS.map((skill) => ({ skill, ratio: (analysis.skills[skill].nclc ?? 0) / target }))}
            size={224}
            stroke={15}
          >
            <span className="ac-fitness__center-value ao-num">
              {reached}/{SKILLS.length}
            </span>
            <span className="ac-fitness__center-label">{t("academy.ui.progress.atTarget")}</span>
          </ActivityRings>
          <div className="ac-table-wrap">
            <table className="ac-skill-table">
              <caption className="visually-hidden">{t("academy.progress.perSkill")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("academy.progress.skill")}</th>
                  <th scope="col" className="num">
                    {t("academy.progress.current")}
                  </th>
                  <th scope="col">{t("academy.progress.gap")}</th>
                  <th scope="col">{t("academy.progress.trend")}</th>
                </tr>
              </thead>
              <tbody>
                {SKILLS.map((skill) => {
                  const row = analysis.skills[skill];
                  const Icon = TREND_ICON[row.trend];
                  return (
                    <tr key={skill} className={`ac-skill--${skill}`}>
                      <th scope="row">
                        <span className="ac-ring-legend__swatch" aria-hidden="true" />
                        {t(`academy.skill.${skill}`)}
                      </th>
                      <td className="num ao-num">
                        {row.nclc !== null ? (
                          <>
                            <strong>{row.nclc}</strong>
                            <span className="ac-tertiary"> / {target}</span>
                          </>
                        ) : (
                          t("academy.progress.notMeasured")
                        )}
                      </td>
                      <td className={row.gap !== null && row.gap > 0 ? "ac-gap ac-gap--missing" : row.gap !== null ? "ac-gap ac-gap--ok" : "ac-gap"}>
                        {row.gap === null
                          ? "—"
                          : row.gap > 0
                            ? t("academy.progress.gapMissing", { count: row.gap })
                            : t("academy.progress.gapReached")}
                      </td>
                      <td>
                        <span className={`ac-trend ac-trend--${row.trend}`}>
                          <Icon aria-hidden size={15} />
                          {t(`academy.progress.trends.${row.trend}`)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </GlassCard>

      <div className="ac-grid">
        <GlassCard
          as="section"
          className={`ac-card ac-span-7 ac-verdict ac-verdict--${analysis.verdict}`}
          aria-labelledby="academy-verdict"
        >
          <CardTitle id="academy-verdict" icon={<VerdictIcon size={16} />} tone={VERDICT_TONE[analysis.verdict]}>
            {t("academy.verdict.title")}
          </CardTitle>
          <div className="ac-verdict__line">
            <span className="ac-verdict__badge">{t(`academy.verdict.${analysis.verdict}`)}</span>
            {analysis.daysLeft !== null && analysis.daysLeft >= 0 && (
              <span className="ac-caption">{t("academy.verdict.daysLeft", { count: analysis.daysLeft })}</span>
            )}
          </div>
          <p>{t(`academy.verdict.${analysis.verdict}Text`, { n: target })}</p>
          <h3 className="ac-label">{t("academy.critical.title")}</h3>
          {analysis.critical.length === 0 ? (
            <p className="ok">{t("academy.critical.none")}</p>
          ) : (
            <ul className="ac-critical">
              {analysis.critical.map((point, index) => (
                <li
                  key={`${point.code}-${point.skill ?? ""}-${index}`}
                  className={`ac-critical__item ac-critical__item--${point.code === "exam_too_soon" || point.code === "below_target" ? "high" : "mid"}`}
                >
                  <AlertTriangle aria-hidden size={15} />
                  <span>{criticalText(point, t, criterionLabel)}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="ac-footnote">{t("academy.verdict.estimate")}</p>
        </GlassCard>
        <div className="ac-span-5 ac-side-stats">
          <StatTile label={t("academy.progress.xp")} value={t("academy.progress.xpValue", { xp: progress.xp })} icon={<Sparkles size={16} />} tone="warning" />
          <StatTile
            label={t("academy.progress.streak")}
            value={t("academy.progress.streakValue", { count: progress.streakDays })}
            icon={<Flame size={16} />}
            tone="eo"
          />
          <RankTile title={t("academy.progress.rankPlatform")} block={progress.platform} icon={<Medal size={16} />} />
          <RankTile title={t("academy.progress.rankAgency")} block={progress.agency} icon={<Trophy size={16} />} />
        </div>
      </div>
      <RankScoreCard rank={query.data?.status === 200 ? query.data.data.rank : null} />

      <GlassCard as="section" className="ac-card" aria-labelledby="academy-evolution">
        <div className="ac-card__head">
          <CardTitle id="academy-evolution" icon={<TrendingUp size={16} />} tone="success">
            {t("academy.progress.evolution")}
          </CardTitle>
        </div>
        <EvolutionChart points={progress.evolution} target={target} />
      </GlassCard>

      <GlassCard as="section" className="ac-card" aria-labelledby="academy-report">
        <CardTitle id="academy-report" icon={<FileDown size={16} />} tone="info">
          {t("academy.report.title")}
        </CardTitle>
        <ReportButton />
      </GlassCard>
    </div>
  );
}
